import classNames from "classnames";
import { SensorKey, SensorView } from "../sensorHealth.utility";
import { SensorCell } from "./SensorCell";

type SensorBoardPanelProps = {
  boardId: number;
  responding: boolean;
  sensors: SensorView[];
  dimmedKeys?: Set<SensorKey>;
  onSelectSensor: (sensor: SensorView) => void;
};

const StatusPill = ({ responding }: { responding: boolean }) =>
  responding ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
      Reporting
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
      <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden />
      Not responding
    </span>
  );

/**
 * A board and its sensors. A responding board is a calm white card; one that has gone quiet is edged
 * and headed in red with a plain-language note, so the thing worth acting on can't be mistaken for a
 * healthy one at a glance.
 */
export const SensorBoardPanel = ({
  boardId,
  responding,
  sensors,
  dimmedKeys,
  onSelectSensor,
}: SensorBoardPanelProps) => (
  <section
    className={classNames(
      "overflow-hidden rounded-2xl border bg-white shadow-sm",
      responding ? "border-gray-200/80" : "border-red-200",
    )}
  >
    <div
      className={classNames(
        "flex items-center justify-between gap-3 px-4 py-3",
        responding ? "border-b border-gray-100" : "border-b border-red-100 bg-red-50/50",
      )}
    >
      <div className="flex items-baseline gap-2.5">
        <h3 className="font-mono text-sm font-semibold tracking-tight text-gray-900">Board {boardId}</h3>
        <span className="text-xs text-gray-400">
          {sensors.length} {sensors.length === 1 ? "sensor" : "sensors"}
        </span>
      </div>
      <StatusPill responding={responding} />
    </div>

    <div className="p-4">
      {!responding && (
        <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-inset ring-red-100">
          This board stopped reporting. The readings below are the last ones received before it went quiet.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {sensors.map((sensor) => (
          <SensorCell
            key={sensor.key}
            sensor={sensor}
            dimmed={dimmedKeys ? !dimmedKeys.has(sensor.key) : false}
            onClick={onSelectSensor}
          />
        ))}
      </div>
    </div>
  </section>
);
